import monomarkUrl from "@enrein/design-system/assets/enrein-monomark-v4.png";
import wordmarkUrl from "@enrein/design-system/assets/enrein-wordmark-v4.png";
import { useCallback, type KeyboardEvent, type ReactNode } from "react";
import type { V10ClientTabId } from "../types.js";
import { V10_TAB_LABELS } from "../adapters/v10-client-fixtures.js";

export function V10ClientTabs({
  activeTab,
  onTabChange,
  tabIds,
}: {
  readonly activeTab: V10ClientTabId;
  readonly onTabChange: (tab: V10ClientTabId) => void;
  readonly tabIds: readonly V10ClientTabId[];
}) {
  const onKeyDown = useCallback(
    (event: KeyboardEvent<HTMLButtonElement>, index: number) => {
      let next = index;
      if (event.key === "ArrowRight") next = (index + 1) % tabIds.length;
      if (event.key === "ArrowLeft") next = (index - 1 + tabIds.length) % tabIds.length;
      if (event.key === "Home") next = 0;
      if (event.key === "End") next = tabIds.length - 1;
      if (next !== index) {
        event.preventDefault();
        onTabChange(tabIds[next]!);
        const buttons = event.currentTarget.parentElement?.querySelectorAll<HTMLButtonElement>('[role="tab"]');
        buttons?.[next]?.focus();
      }
    },
    [onTabChange, tabIds],
  );

  return (
    <div
      className="v10-cl-tabs"
      role="tablist"
      aria-label="Client Link tabs"
      style={{ gridTemplateColumns: `repeat(${tabIds.length}, minmax(0, 1fr))` }}
    >
      {tabIds.map((tabId, index) => {
        const selected = activeTab === tabId;
        return (
          <button
            key={tabId}
            type="button"
            role="tab"
            id={`v10-tab-btn-${tabId}`}
            aria-selected={selected}
            aria-controls={`v10-tab-${tabId}`}
            tabIndex={selected ? 0 : -1}
            onClick={() => onTabChange(tabId)}
            onKeyDown={(event) => onKeyDown(event, index)}
          >
            {V10_TAB_LABELS[tabId]}
          </button>
        );
      })}
    </div>
  );
}

export function V10ClientFrame({
  statusLabel,
  subtitle = "Review room",
  businessDisplayName,
  children,
}: {
  readonly statusLabel: string;
  readonly subtitle?: string;
  readonly businessDisplayName?: string | null;
  readonly children: ReactNode;
}) {
  return (
    <div className="v10-cl-frame">
      <header className="v10-cl-header">
        <div className="v10-cl-client-brand">
          <img className="v10-cl-brand-mono" src={monomarkUrl} alt="" width={30} height={30} />
          <img className="v10-cl-brand-wordmark" src={wordmarkUrl} alt="Enrein" width={86} height={22} />
        </div>
        <div className="v10-cl-header-copy">
          <p>{businessDisplayName ?? "Client Link"} · {subtitle}</p>
        </div>
        <span className="v10-cl-pill">{statusLabel}</span>
      </header>
      {children}
    </div>
  );
}
